import { reactive } from 'vue';
import type { QueueRecord } from './db';
import { queue as store } from './db';
import { runSubmission } from './submission';
import { ApiClientError } from './api';

/**
 * The offline submission queue.
 *
 * A submission that could not be sent is kept here, shown as "N pending" and
 * flushed when connectivity returns — never silently dropped. Exactly-once is
 * the Worker's job: every attempt carries the record id as its
 * `Idempotency-Key`, so a double flush replays the first result instead of
 * creating a second applicant.
 */
export const queueState = reactive<{ pending: QueueRecord[]; flushing: boolean; lastError: string | null }>({
  pending: [],
  flushing: false,
  lastError: null,
});

export const SYNC_TAG = 'mbs-submissions';

export async function refreshQueue(): Promise<void> {
  const all = await store.all();
  queueState.pending = all.filter((r) => r.status === 'pending');
}

export async function enqueue(record: QueueRecord): Promise<void> {
  await store.put(record);
  await refreshQueue();
  await requestBackgroundSync();
}

export async function updateRecord(record: QueueRecord): Promise<void> {
  record.updated_at = new Date().toISOString();
  await store.put(record);
  await refreshQueue();
}

export async function discard(id: string): Promise<void> {
  await store.remove(id);
  await refreshQueue();
}

/** Ask the service worker to retry for us the moment the device is back online. */
export async function requestBackgroundSync(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sync = (registration as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } })
      .sync;
    await sync?.register(SYNC_TAG);
  } catch {
    // Background Sync is not everywhere (notably iOS). The `online` listener in
    // main.ts covers those browsers.
  }
}

export async function flushQueue(): Promise<void> {
  if (queueState.flushing) return;
  queueState.flushing = true;
  queueState.lastError = null;
  try {
    const records = (await store.all()).filter((r) => r.status === 'pending');
    for (const record of records) {
      try {
        record.attempts += 1;
        const result = await runSubmission(record, {
          onProgress: async (updated) => {
            await store.put({ ...updated, updated_at: new Date().toISOString() });
          },
        });
        record.result = result;
        record.status = 'done';
        record.updated_at = new Date().toISOString();
        await store.put(record);
        // Keep completed records briefly so the UI can confirm what went
        // through; they are cleared on the next successful flush.
        window.setTimeout(() => void store.remove(record.id), 60_000);
      } catch (error) {
        record.last_error =
          error instanceof ApiClientError ? error.payload.message : String(error);
        record.updated_at = new Date().toISOString();
        // A validation failure will never succeed on retry; anything else stays
        // queued.
        if (error instanceof ApiClientError && error.payload.code === 'validation_failed') {
          record.status = 'failed';
        }
        await store.put(record);
        queueState.lastError = record.last_error;
        if (error instanceof ApiClientError && error.offline) break;
      }
    }
  } finally {
    queueState.flushing = false;
    await refreshQueue();
  }
}
